import type {
  ChatMessage,
  CalendarConnectionPrompt,
  CalendarContextSummary,
  CalendarContextSummaryDay,
  Direction,
  FeedbackType,
  MessageType,
  RawCalendarConnectionPrompt,
  RawCalendarContextSummary,
  RawCalendarContextSummaryDay,
  RawInsightTable,
  RawSearchSupportInsight,
  RawSummary,
  RawUnsupportedRequest,
  SearchSupportInsight,
  Summary,
  UnsupportedRequest
} from '../types/messages';

type RawChatMessage = {
  id: string;
  direction: Direction;
  text: string;
  created_at: string;
  type?: MessageType;
  bundle_ids?: number[];
  item_ids?: number[];
  summary?: RawSummary;
  insight?: RawSearchSupportInsight;
  unsupported_request?: RawUnsupportedRequest;
  calendar_connection_prompt?: RawCalendarConnectionPrompt;
  calendar_context_summary?: RawCalendarContextSummary;
  editable?: boolean;
  order_id?: number;
  approval_required?: boolean;
  input_disabled?: boolean;
  proposal_change_disabled?: boolean;
  feedback?: FeedbackType;
  feedback_comment_submitted?: boolean;
};

type MessageOptions = {
  isNew?: boolean;
  isNewlyCompleted?: boolean;
};

export const toSummary = (raw: RawSummary): Summary => ({
  items: raw.items.map(item => ({
    category: item.category,
    label: item.label,
    text: item.text,
    price: item.price,
    priceWithTax: item.price_with_tax
  })),
  totalPrice: raw.total_price,
  totalPriceWithTax: raw.total_price_with_tax,
  hasSelection: raw.has_selection
});

const toInsightTable = (raw: RawInsightTable) => ({
  columns: raw.columns,
  rows: raw.rows,
  highlightedDates: raw.highlighted_dates
});

export const toSearchSupportInsight = (raw: RawSearchSupportInsight): SearchSupportInsight => ({
  title: raw.title,
  summary: raw.summary,
  metrics: raw.metrics,
  table: raw.table ? toInsightTable(raw.table) : undefined,
  nextAction: raw.next_action,
  coverage: raw.coverage,
  components: raw.components,
  missingRequirements: raw.missing_requirements
});

export const toUnsupportedRequest = (raw: RawUnsupportedRequest): UnsupportedRequest => ({
  title: raw.title,
  supportedProducts: raw.supported_products,
  description: raw.description,
  dashboardPath: raw.dashboard_path,
  dashboardLabel: raw.dashboard_label
});

export const toCalendarConnectionPrompt = (raw: RawCalendarConnectionPrompt): CalendarConnectionPrompt => ({
  title: raw.title,
  description: raw.description,
  actionPath: raw.action_path,
  actionLabel: raw.action_label
});

export const toCalendarContextSummaryDay = (raw: RawCalendarContextSummaryDay): CalendarContextSummaryDay => ({
  date: raw.date,
  status: raw.status,
  blockingEventCount: raw.blocking_event_count,
  timedEventsCount: raw.timed_events_count,
  allDayBlockers: raw.all_day_blockers,
  totalBusyMinutes: raw.total_busy_minutes,
  longestFreeSlotMinutes: raw.longest_free_slot_minutes,
  busySummaries: raw.busy_summaries,
  proposalFeasibility: raw.proposal_feasibility,
  blockerLevel: raw.blocker_level,
  reasonSummary: raw.reason_summary,
  recommendedAction: raw.recommended_action
});

export const toCalendarContextSummary = (raw: RawCalendarContextSummary): CalendarContextSummary => ({
  period: raw.period,
  candidateDates: raw.candidate_dates,
  suggestedDates: raw.suggested_dates,
  overallSummary: raw.overall_summary,
  days: raw.days.map(day => toCalendarContextSummaryDay(day))
});

export const toChatMessage = (message: RawChatMessage, options: MessageOptions = {}): ChatMessage => ({
  id: message.id,
  direction: message.direction,
  text: message.text,
  createdAt: message.created_at,
  type: message.type,
  proposalBundleIds: message.bundle_ids,
  proposalItemIds: message.item_ids,
  summary: message.summary ? toSummary(message.summary) : undefined,
  insight: message.insight ? toSearchSupportInsight(message.insight) : undefined,
  unsupportedRequest: message.unsupported_request ? toUnsupportedRequest(message.unsupported_request) : undefined,
  calendarConnectionPrompt: message.calendar_connection_prompt
    ? toCalendarConnectionPrompt(message.calendar_connection_prompt)
    : undefined,
  calendarContextSummary: message.calendar_context_summary
    ? toCalendarContextSummary(message.calendar_context_summary)
    : undefined,
  editable: message.editable,
  orderId: message.order_id,
  approvalRequired: message.approval_required,
  inputDisabled: message.input_disabled,
  proposalChangeDisabled: message.proposal_change_disabled,
  isNew: options.isNew,
  isNewlyCompleted: options.isNewlyCompleted,
  feedback: message.feedback ?? null,
  feedbackCommentSubmitted: message.feedback_comment_submitted ?? false
});
